import React, { useEffect, useMemo, useState } from 'react';
import { useAtom } from 'jotai';
import { View, Text, Pressable, ScrollView, ActivityIndicator } from '../lib/react-native-compat';
import { selectedJobAtom, userEmailAtom } from '../store/company';
import {
    loadStaffDirectory, loadJobTeam, loadJobTeamDefaults,
    saveJobTeamMember, deleteJobTeamMember,
} from '../db/company';
import { showToast } from '../helpers/toast';

// Job Team tab: who is filling each role on the selected job. People come from
// JDE's employee address book (active staff only); assignments live in
// PC.Job_Team. Jobs with no saved team show one-click defaults from the K2
// setup request (PM, superintendent, ...).

// Starting simple: one flat list of core roles. Any role can hold several
// people (Foremen by discipline, split-front supers, ...). The fuller
// category catalog can come back once the team pages are in daily use.
const ROLES = [
    { role: 'Project Manager' },
    { role: 'Assistant Project Manager' },
    { role: 'Construction Manager' },
    { role: 'Superintendent' },
    { role: 'Assistant Superintendent' },
    { role: 'Foreman / Crew Lead', note: 'By discipline — pipe, civil, E&I, coating, ...' },
    { role: 'Project Controls Lead' },
    { role: 'Field Safety Rep' },
];

const inputBase = {
    fontSize: 14, color: '#0F172A', backgroundColor: '#FFFFFF',
    border: '1px solid #CBD5E1', borderRadius: 6,
    paddingTop: 7, paddingBottom: 7, paddingLeft: 10, paddingRight: 10,
    outline: 'none', width: '100%', boxSizing: 'border-box',
};

const JobTeam = () => {
    const [selectedJob] = useAtom(selectedJobAtom);
    const [userEmail] = useAtom(userEmailAtom);

    const [directory, setDirectory] = useState(null);   // all active staff
    const [team, setTeam] = useState(null);             // saved assignments
    const [defaults, setDefaults] = useState([]);       // K2 setup suggestions
    const [loading, setLoading] = useState(false);
    const [pickerRole, setPickerRole] = useState(null); // role whose picker is open
    const [search, setSearch] = useState('');
    const [savingRole, setSavingRole] = useState(null);

    useEffect(() => {
        loadStaffDirectory()
            .then((res) => setDirectory(res?.staff || []))
            .catch(() => showToast('Could not load the employee directory.', 'error'));
    }, []);

    useEffect(() => {
        if (!selectedJob) return;
        setTeam(null);
        setDefaults([]);
        setPickerRole(null);
        setLoading(true);
        Promise.all([
            loadJobTeam({ buId: String(selectedJob) }),
            loadJobTeamDefaults({ buId: String(selectedJob) }).catch(() => ({ defaults: [] })),
        ]).then(([t, d]) => {
            setTeam(t?.team || []);
            setDefaults(d?.defaults || []);
        }).catch(() => {
            setTeam([]);
            showToast('Could not load the job team.', 'error');
        }).finally(() => setLoading(false));
    }, [selectedJob]);

    const byRole = useMemo(() => {
        const m = {};
        (team || []).forEach((r) => {
            (m[r.TeamRole] = m[r.TeamRole] || []).push(r);
        });
        return m;
    }, [team]);

    const assignedCount = (team || []).length;

    const matches = useMemo(() => {
        if (!directory) return [];
        const q = search.trim().toLowerCase();
        const pool = q
            ? directory.filter((s) =>
                String(s.StaffName || '').toLowerCase().includes(q) ||
                String(s.StaffTitle || '').toLowerCase().includes(q))
            : directory;
        return pool.slice(0, 12);
    }, [directory, search]);

    const assign = async (role, person) => {
        if (!selectedJob || savingRole) return;
        setSavingRole(role);
        try {
            const res = await saveJobTeamMember({
                buId: String(selectedJob),
                staffNumber: person.StaffNumber,
                staffName: person.StaffName,
                staffTitle: person.StaffTitle ?? null,
                staffEmail: person.StaffEmail ?? null,
                teamRole: role,
                roleCategory: null,
                userEmail,
            });
            setTeam(res?.team || []);
            setPickerRole(null);
            setSearch('');
        } catch (e) {
            showToast('Save failed — try again.', 'error');
        } finally {
            setSavingRole(null);
        }
    };

    const remove = async (row) => {
        try {
            await deleteJobTeamMember({ rowId: row.RowID, userEmail });
            setTeam((t) => (t || []).filter((r) => r.RowID !== row.RowID));
        } catch (e) {
            showToast('Remove failed — try again.', 'error');
        }
    };

    const changeRole = async (row, newRole) => {
        if (!newRole || newRole === row.TeamRole) return;
        const person = {
            StaffNumber: row.StaffNumber, StaffName: row.StaffName,
            StaffTitle: row.StaffTitle, StaffEmail: row.StaffEmail,
        };
        await assign(newRole, person);
        await remove(row);
    };

    if (!selectedJob) {
        return (
            <View style={{ padding: 24 }}>
                <Text style={{ fontSize: 15, color: '#64748B' }}>Select a job to manage its team.</Text>
            </View>
        );
    }

    // Rows saved under a role that's no longer in the list still show up
    // (appended at the bottom) so nobody's assignment silently disappears.
    const extraRoles = Object.keys(byRole)
        .filter((r) => !ROLES.some((x) => x.role === r))
        .map((role) => ({ role }));
    const visibleRoles = [...ROLES, ...extraRoles];
    const allRoles = visibleRoles.map((r) => r.role);

    return (
        <ScrollView style={{ flex: 1 }} contentContainerStyle={{ padding: 20, maxWidth: 980 }}>
            <View style={{ flexDirection: 'row', alignItems: 'baseline', columnGap: 12, marginBottom: 4 }}>
                <Text style={{ fontSize: 20, fontWeight: '700', color: '#0F172A' }}>Job Team</Text>
                <Text style={{ fontSize: 13, color: '#64748B' }}>
                    {String(selectedJob)} — {assignedCount} assigned
                </Text>
                {loading && <ActivityIndicator size="small" color="#64748B" />}
            </View>
            <Text style={{ fontSize: 12, color: '#64748B', marginBottom: 16 }}>
                People come from the JDE employee address book (active employees only). Name and title
                are shown as of assignment; suggested rows come from the job&apos;s K2 setup request.
            </Text>

            <View
                style={{
                    backgroundColor: '#FFFFFF', border: '1px solid #E2E8F0', borderRadius: 8,
                    marginBottom: 14, paddingTop: 6, paddingBottom: 6, paddingLeft: 16, paddingRight: 16,
                }}
            >
                {visibleRoles.map(({ role, note }) => {
                        const rows = byRole[role] || [];
                        const suggestions = rows.length === 0
                            ? defaults.filter((d) => d.TeamRole === role &&
                                !(team || []).some((t) => t.StaffNumber === d.StaffNumber && t.TeamRole === role))
                            : [];
                        const pickerOpen = pickerRole === role;
                        return (
                            <View key={role} style={{ borderTop: '1px solid #F1F5F9', paddingTop: 8, paddingBottom: 8 }}>
                                <View style={{ flexDirection: 'row', flexWrap: 'wrap', alignItems: 'flex-start', columnGap: 12 }}>
                                    <View style={{ width: 260, paddingTop: 4 }}>
                                        <Text style={{ fontSize: 13, fontWeight: '600', color: '#334155' }}>{role}</Text>
                                        {note && <Text style={{ fontSize: 11, color: '#94A3B8' }}>{note}</Text>}
                                    </View>

                                    <View style={{ flex: 1, minWidth: 320 }}>
                                        {rows.map((row) => (
                                            <View
                                                key={row.RowID}
                                                style={{
                                                    flexDirection: 'row', alignItems: 'center', columnGap: 10,
                                                    backgroundColor: '#F8FAFC', border: '1px solid #E2E8F0', borderRadius: 6,
                                                    paddingTop: 6, paddingBottom: 6, paddingLeft: 10, paddingRight: 6,
                                                    marginBottom: 6,
                                                }}
                                            >
                                                <View style={{ flex: 1 }}>
                                                    <Text style={{ fontSize: 14, fontWeight: '600', color: '#0F172A' }}>{row.StaffName}</Text>
                                                    <Text style={{ fontSize: 12, color: '#64748B' }}>{row.StaffTitle || 'No JDE title'}</Text>
                                                </View>
                                                <select
                                                    value={row.TeamRole}
                                                    onChange={(e) => changeRole(row, e.target.value)}
                                                    title="Move to a different role"
                                                    style={{
                                                        fontSize: 12, color: '#64748B', border: '1px solid #CBD5E1',
                                                        borderRadius: 6, padding: '4px 6px', backgroundColor: '#FFFFFF',
                                                        maxWidth: 190,
                                                    }}
                                                >
                                                    {allRoles.map((r) => <option key={r} value={r}>{r}</option>)}
                                                </select>
                                                <Pressable
                                                    onPress={() => remove(row)}
                                                    title="Remove from team"
                                                    style={{ paddingTop: 2, paddingBottom: 2, paddingLeft: 8, paddingRight: 8, cursor: 'pointer' }}
                                                >
                                                    <Text style={{ fontSize: 16, color: '#94A3B8' }}>×</Text>
                                                </Pressable>
                                            </View>
                                        ))}

                                        {suggestions.map((s) => (
                                            <View
                                                key={`sug-${s.StaffNumber}`}
                                                style={{
                                                    flexDirection: 'row', alignItems: 'center', columnGap: 10,
                                                    border: '1px dashed #CBD5E1', borderRadius: 6,
                                                    paddingTop: 6, paddingBottom: 6, paddingLeft: 10, paddingRight: 10,
                                                    marginBottom: 6,
                                                }}
                                            >
                                                <View style={{ flex: 1 }}>
                                                    <Text style={{ fontSize: 14, color: '#334155' }}>{s.StaffName}</Text>
                                                    <Text style={{ fontSize: 12, color: '#94A3B8' }}>
                                                        {(s.StaffTitle || 'No JDE title')} — suggested from job setup
                                                    </Text>
                                                </View>
                                                <Pressable
                                                    onPress={() => assign(role, s)}
                                                    disabled={savingRole === role}
                                                    style={{
                                                        border: '1px solid #1D4ED8', borderRadius: 6, cursor: 'pointer',
                                                        paddingTop: 4, paddingBottom: 4, paddingLeft: 12, paddingRight: 12,
                                                    }}
                                                >
                                                    <Text style={{ fontSize: 12, fontWeight: '600', color: '#1D4ED8' }}>
                                                        {savingRole === role ? 'Assigning…' : 'Assign'}
                                                    </Text>
                                                </Pressable>
                                            </View>
                                        ))}

                                        {pickerOpen ? (
                                            <View style={{ marginBottom: 6 }}>
                                                <input
                                                    type="text"
                                                    autoFocus
                                                    value={search}
                                                    placeholder="Search name or title…"
                                                    onChange={(e) => setSearch(e.target.value)}
                                                    onKeyDown={(e) => { if (e.key === 'Escape') { setPickerRole(null); setSearch(''); } }}
                                                    style={inputBase}
                                                />
                                                <View style={{ border: '1px solid #E2E8F0', borderRadius: 6, marginTop: 4, backgroundColor: '#FFFFFF', maxHeight: 280, overflow: 'auto' }}>
                                                    {directory === null && (
                                                        <Text style={{ fontSize: 12, color: '#94A3B8', padding: 8 }}>Loading directory…</Text>
                                                    )}
                                                    {directory !== null && matches.length === 0 && (
                                                        <Text style={{ fontSize: 12, color: '#94A3B8', padding: 8 }}>No active employees match.</Text>
                                                    )}
                                                    {matches.map((s) => (
                                                        <Pressable
                                                            key={s.StaffNumber}
                                                            onPress={() => assign(role, s)}
                                                            style={{
                                                                flexDirection: 'row', alignItems: 'baseline', columnGap: 8,
                                                                paddingTop: 6, paddingBottom: 6, paddingLeft: 10, paddingRight: 10,
                                                                cursor: 'pointer', borderBottom: '1px solid #F1F5F9',
                                                            }}
                                                        >
                                                            <Text style={{ fontSize: 13, fontWeight: '600', color: '#0F172A' }}>{s.StaffName}</Text>
                                                            <Text style={{ fontSize: 12, color: '#64748B' }}>{s.StaffTitle || ''}</Text>
                                                        </Pressable>
                                                    ))}
                                                </View>
                                                <Pressable onPress={() => { setPickerRole(null); setSearch(''); }} style={{ marginTop: 4, cursor: 'pointer', alignSelf: 'flex-start' }}>
                                                    <Text style={{ fontSize: 12, color: '#64748B' }}>Cancel</Text>
                                                </Pressable>
                                            </View>
                                        ) : (
                                            <Pressable
                                                onPress={() => { setPickerRole(role); setSearch(''); }}
                                                style={{ cursor: 'pointer', alignSelf: 'flex-start', paddingTop: 2, paddingBottom: 2 }}
                                            >
                                                <Text style={{ fontSize: 12, fontWeight: '600', color: '#1D4ED8' }}>+ Add person</Text>
                                            </Pressable>
                                        )}
                                    </View>
                                </View>
                            </View>
                    );
                })}
            </View>
        </ScrollView>
    );
};

export default JobTeam;
